import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import ApiClient from '../utils/ApiClient';

const TIER_STYLES = {
    'Standard-Size': {
        label: 'Standard Size',
        badge: 'bg-green-100 text-green-800 border-green-300',
        accent: 'from-green-500 to-emerald-600',
    },
    'Oversize': {
        label: 'Oversize',
        badge: 'bg-amber-100 text-amber-800 border-amber-300',
        accent: 'from-amber-500 to-orange-600',
    },
    'Extra-Large': {
        label: 'Extra Large',
        badge: 'bg-red-100 text-red-800 border-red-300',
        accent: 'from-red-500 to-rose-600',
    },
};

const DEFAULT_TIER_STYLE = {
    label: null, // fall back to the raw tier string
    badge: 'bg-gray-100 text-gray-800 border-gray-300',
    accent: 'from-slate-500 to-gray-600',
};

const getTierStyle = (tier) => TIER_STYLES[tier] || DEFAULT_TIER_STYLE;

function ProductSizeTier() {
    const [asin, setAsin] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();

        if (!asin.trim()) {
            setError('Please enter an ASIN');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await ApiClient.get(`/api/product-size/${encodeURIComponent(asin.trim())}`);
            const data = response.data;

            if (data.success) {
                setResult(data);
            } else {
                setError(data.message || 'Could not determine size tier for this ASIN.');
            }
        } catch (err) {
            setError('An error occurred while fetching the size tier. Please try again.');
            console.error('Error fetching product size tier:', err);
        } finally {
            setLoading(false);
        }
    };

    const fmt = (v, suffix = '') => (v === null || v === undefined ? 'N/A' : `${v}${suffix}`);

    const tierStyle = result ? getTierStyle(result.sizeTier) : null;
    const isOfficial = result && result.tierSource === 'amazon_official';

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 py-10">
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Amazon Product Size Tier
                    </h1>
                    <p className="text-gray-600">
                        Enter an ASIN to see Amazon's size classification: Standard Size, Oversize, or Extra Large.
                    </p>
                </div>

                {/* Search Form */}
                <div className="max-w-2xl mx-auto mb-8">
                    <form onSubmit={handleSearch} className="bg-white rounded-lg shadow p-6">
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={asin}
                                onChange={(e) => setAsin(e.target.value.toUpperCase())}
                                placeholder="Enter ASIN (e.g., B07XYZ1234)"
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                                maxLength="10"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Looking up...
                                    </>
                                ) : (
                                    <>
                                        <MagnifyingGlassIcon className="h-5 w-5" />
                                        Look Up
                                    </>
                                )}
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-3">
                            ASIN format: 10 characters, usually starting with B
                        </p>
                    </form>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="max-w-2xl mx-auto mb-8">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-red-800 font-medium">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Result */}
                {result && tierStyle && (
                    <div className="max-w-3xl mx-auto">
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            {/* Tier Banner */}
                            <div className={`bg-gradient-to-r ${tierStyle.accent} px-6 py-8 text-center`}>
                                <p className="text-white/80 text-sm font-medium uppercase tracking-wide mb-1">
                                    Amazon Size Tier
                                </p>
                                <p className="text-4xl font-bold text-white">{tierStyle.label || result.sizeTier}</p>
                                <div className="mt-3">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white">
                                        {isOfficial ? (
                                            <>
                                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Amazon official
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Estimated from dimensions
                                            </>
                                        )}
                                    </span>
                                </div>
                                {isOfficial && result.amazonTier && (
                                    <p className="text-white/70 text-xs mt-2">
                                        Amazon classification: {result.amazonTier}
                                    </p>
                                )}
                                <p className="text-white/80 text-sm mt-2">ASIN: {result.asin}</p>
                            </div>

                            <div className="p-6">
                                {/* Source note */}
                                <div className="mb-6 text-sm text-gray-500">
                                    {isOfficial ? (
                                        <p>
                                            This tier is Amazon's{' '}
                                            <span className="font-medium text-gray-700">official classification</span>{' '}
                                            from the FBA Fee Preview report
                                            {result.hasDimensions ? ', shown alongside the catalog dimensions below.' : '.'}
                                        </p>
                                    ) : (
                                        <p>
                                            Amazon has not assigned an official tier for this ASIN, so it was{' '}
                                            <span className="font-medium text-gray-700">estimated from catalog dimensions</span>{' '}
                                            ({result.dataSource === 'amazon_live' ? 'live Amazon fetch' : 'cached catalog data'},{' '}
                                            {result.dimensionSource === 'package' ? 'package dimensions' : 'item dimensions'}).
                                        </p>
                                    )}
                                </div>

                                {result.hasDimensions && (
                                    <>
                                        {/* Measured dimensions */}
                                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                            Measured Dimensions
                                        </h3>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                                            <Stat label="Length" value={fmt(result.lengthIn, '"')} />
                                            <Stat label="Width" value={fmt(result.widthIn, '"')} />
                                            <Stat label="Height" value={fmt(result.heightIn, '"')} />
                                            <Stat label="Weight" value={fmt(result.weightLbs, ' lb')} />
                                        </div>

                                        {/* Derived classification inputs */}
                                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                            Classification Inputs
                                        </h3>
                                        <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
                                            <Row label="Longest side" value={fmt(result.longestSideIn, '"')} />
                                            <Row label="Median side" value={fmt(result.medianSideIn, '"')} />
                                            <Row label="Shortest side" value={fmt(result.shortestSideIn, '"')} />
                                            <Row label="Girth (2 × (median + shortest))" value={fmt(result.girthIn, '"')} />
                                            <Row label="Length + girth" value={fmt(result.lengthPlusGirthIn, '"')} />
                                            <Row label="Dimensional weight (L×W×H ÷ 139)" value={fmt(result.dimensionalWeightLbs, ' lb')} />
                                            <Row label="Shipping weight (greater of unit / dim)" value={fmt(result.shippingWeightLbs, ' lb')} />
                                        </div>
                                    </>
                                )}

                                <p className="text-xs text-gray-400 mt-5">
                                    {isOfficial
                                        ? "Official tier comes from Amazon's FBA Fee Preview report and is only available for FBA-eligible ASINs. The dimension figures, when shown, are informational."
                                        : "Estimated from Amazon's US FBA size-tier thresholds — Standard-Size: ≤ 20 lb and ≤ 18 / 14 / 8 in (longest / median / shortest); Oversize: ≤ 50 lb, ≤ 59 in longest, ≤ 33 in median, ≤ 130 in length + girth; anything larger is Extra-Large. May differ from Amazon's official classification."}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-center">
            <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
            <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-gray-600">{label}</span>
            <span className="text-sm font-semibold text-gray-900">{value}</span>
        </div>
    );
}

export default ProductSizeTier;
